import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  LayoutDashboard,
  AlertTriangle,
  Recycle,
  ChevronDown,
  ChevronRight,
  Home,
  ClipboardList,
  ScanLine,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NAV_TITLES = {
  '/gestion': 'Resumen',
  '/gestion/incidencias': 'Gestión de Incidencias',
  '/gestion/ecoparque': 'Ecoparque · Dashboard',
  '/gestion/ecoparque/registrar': 'Ecoparque · Registrar entrega',
};

export default function GestionLayout() {
  const location = useLocation();
  const auth = useAuth?.() || {};
  const [openEcoparque, setOpenEcoparque] = useState(
    location.pathname.startsWith('/gestion/ecoparque')
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/gestion/ecoparque')) setOpenEcoparque(true);
    setMobileOpen(false);
  }, [location.pathname]);

  const title = NAV_TITLES[location.pathname] || 'Gestión Interna';

  return (
    <div className="min-h-screen bg-[#FAFCFE] flex">
      <Helmet>
        <title>{title} | Cuidemos Los Alcázares®</title>
        <meta
          name="description"
          content="Portal de Gestión Interna de Valoriza: incidencias GIS, KPIs, históricos y dashboard del Ecoparque (PAYT) en Los Alcázares."
        />
      </Helmet>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-72 bg-[#0A2540] text-white flex flex-col transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        data-testid="gestion-sidebar"
      >
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60">Gestión Interna</p>
            <p className="font-display text-xl mt-0.5">Valoriza</p>
          </div>
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg hover:bg-white/10"
            onClick={() => setMobileOpen(false)}
            aria-label="Cerrar menú"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <SidebarLink to="/gestion" end icon={LayoutDashboard} label="Resumen" testid="sidebar-resumen" />
          <SidebarLink
            to="/gestion/incidencias"
            icon={AlertTriangle}
            label="Incidencias"
            testid="sidebar-incidencias"
          />

          {/* Ecoparque colapsable */}
          <div>
            <button
              type="button"
              onClick={() => setOpenEcoparque((v) => !v)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/80 hover:bg-white/5 transition-colors"
              data-testid="sidebar-ecoparque-toggle"
              aria-expanded={openEcoparque}
            >
              <Recycle size={18} />
              <span className="flex-1 text-left">Ecoparque</span>
              {openEcoparque ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {openEcoparque && (
              <div className="mt-1 ml-3 pl-4 border-l border-white/10 space-y-1">
                <SidebarLink
                  to="/gestion/ecoparque"
                  end
                  icon={ClipboardList}
                  label="Dashboard"
                  testid="sidebar-ecoparque-dashboard"
                  small
                />
                <SidebarLink
                  to="/gestion/ecoparque/registrar"
                  icon={ScanLine}
                  label="Registrar entrega"
                  testid="sidebar-ecoparque-registrar"
                  small
                />
              </div>
            )}
          </div>
        </nav>

        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="sidebar-home"
          >
            <Home size={18} /> Volver al inicio
          </Link>
          {auth?.user && (
            <button
              type="button"
              onClick={() => auth.logout?.()}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors"
              data-testid="sidebar-logout"
            >
              <LogOut size={18} /> Cerrar sesión
            </button>
          )}
        </div>
      </aside>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-[#CFE3F5] px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-[#F5F9FC]"
            aria-label="Abrir menú"
          >
            <Menu size={20} className="text-[#0A2540]" />
          </button>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-[#6F8AA8]">Gestión Interna</p>
            <h1 className="font-display text-xl sm:text-2xl text-[#0A2540] truncate" data-testid="gestion-page-title">
              {title}
            </h1>
          </div>
          {auth?.user?.email && (
            <span className="ml-auto hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5F9FC] border border-[#CFE3F5] text-xs text-[#6F8AA8]">
              {auth.user.email}
            </span>
          )}
        </header>
        <main className="flex-1 px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, end, icon: Icon, label, testid, small = false }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-3 ${small ? 'px-3 py-2 text-[13px]' : 'px-3 py-2.5 text-sm'} rounded-xl font-medium transition-colors ${
          isActive
            ? 'bg-white/15 text-white'
            : 'text-white/75 hover:bg-white/5 hover:text-white'
        }`
      }
    >
      <Icon size={small ? 16 : 18} />
      <span className="flex-1 truncate">{label}</span>
    </NavLink>
  );
}
